/**
 * Tradeoff Ledger — Doc 17 §1.4
 *
 * Shows all tradeoff_decisions for a plan version:
 * "why this over that" — who, when, what, why, linked constraint evidence.
 */

import { Scale, ChevronDown, ChevronRight, ShieldAlert, GitMerge, Zap } from 'lucide-react';
import { useState } from 'react';
import type { TradeoffDecision } from '../../types/planning';

// ─── Kind config ──────────────────────────────────────────────────────────────

const KIND_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  violation_override:  { label: 'Violation override',  icon: ShieldAlert, color: 'text-[var(--coral)]' },
  divergence_override: { label: 'Divergence accepted', icon: GitMerge,    color: 'text-[var(--text-ter)]' },
  policy_override:     { label: 'Policy override',     icon: ShieldAlert, color: 'text-[var(--mustard)]' },
  capacity_trade:      { label: 'Capacity trade',      icon: Zap,         color: 'text-[var(--text-ter)]' },
};

function kindConfig(kind: string) {
  return KIND_CONFIG[kind] ?? { label: kind, icon: Scale, color: 'text-[var(--text-sec)]' };
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function TradeoffRow({ decision }: { decision: TradeoffDecision }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = kindConfig(decision.tradeoff_kind);
  const Icon = cfg.icon;
  const payload = decision.payload ?? {};
  const reason = (payload.reason ?? payload.why ?? payload.rationale ?? '') as string;
  const divergenceIds = (payload.divergence_ids ?? payload.evidence_refs?.divergence_ids ?? []) as string[];
  const evaluationId = (payload.constraint_evaluation_id ?? payload.evidence_refs?.constraint_evaluation_id ?? null) as string | null;

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface)] transition-colors text-left"
      >
        {expanded
          ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        }
        <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${cfg.color}`} />
        <span className={`text-xs font-medium ${cfg.color} min-w-[120px]`}>{cfg.label}</span>
        <span className="text-sm text-[var(--text-sec)] truncate flex-1">{reason || '—'}</span>
        <span className="text-xs text-[var(--text-ter)] flex-shrink-0 ml-2">
          {decision.accepted_at
            ? new Date(decision.accepted_at).toLocaleDateString()
            : new Date(decision.created_at).toLocaleDateString()}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-3 pt-1 space-y-2 border-t border-[var(--rule)] bg-[var(--surface)]/50">
          <div className="grid grid-cols-2 gap-3 text-xs">
            {decision.accepted_by && (
              <div>
                <span className="font-medium text-[var(--text-sec)]">Decided by</span>
                <p className="text-[var(--text-ter)] mt-0.5">{decision.accepted_by}</p>
              </div>
            )}
            {decision.accepted_at && (
              <div>
                <span className="font-medium text-[var(--text-sec)]">At</span>
                <p className="text-[var(--text-ter)] mt-0.5">{new Date(decision.accepted_at).toLocaleString()}</p>
              </div>
            )}
            {reason && (
              <div className="col-span-2">
                <span className="font-medium text-[var(--text-sec)]">Rationale</span>
                <p className="text-[var(--text-sec)] mt-0.5">{reason}</p>
              </div>
            )}
            {divergenceIds.length > 0 && (
              <div className="col-span-2">
                <span className="font-medium text-[var(--text-sec)]">Divergences covered</span>
                <p className="text-[var(--text-ter)] font-mono mt-0.5 text-[10px] break-all">
                  {divergenceIds.join(', ')}
                </p>
              </div>
            )}
            {evaluationId && (
              <div className="col-span-2">
                <span className="font-medium text-[var(--text-sec)]">Evaluation</span>
                <p className="text-[var(--text-ter)] font-mono mt-0.5 text-[10px]">{evaluationId}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export interface TradeoffLedgerProps {
  decisions: TradeoffDecision[];
}

export function TradeoffLedger({ decisions }: TradeoffLedgerProps) {
  if (decisions.length === 0) {
    return (
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="h-4 w-4 text-[var(--text-ter)]" />
          <h2 className="text-sm font-semibold text-[var(--text-pri)]">Tradeoff Ledger</h2>
        </div>
        <p className="text-sm text-[var(--text-ter)]">
          No tradeoff decisions recorded yet. Accept divergences or override policy violations to populate this ledger.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <Scale className="h-4 w-4 text-[var(--text-ter)]" />
          Tradeoff Ledger
        </h2>
        <span className="text-xs text-[var(--text-ter)]">{decisions.length} decision{decisions.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="space-y-1.5">
        {decisions.map((d) => (
          <TradeoffRow key={d.id} decision={d} />
        ))}
      </div>
    </div>
  );
}
